import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LEGAL_DOCS } from "../lib/legal";

export default function Legal() {
  const { slug } = useParams();
  const doc = LEGAL_DOCS[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!doc) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-ivory text-navy">
      <header className="border-b border-black/10 sticky top-0 bg-ivory/90 backdrop-blur-xl z-10">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-logo">
            <span className="font-serif text-2xl text-navy">KRV</span>
            <span className="font-serif text-2xl text-gold-deep">Nexus</span>
          </Link>
          <Link to="/" data-testid="legal-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
            <ArrowLeft size={16} /> Back to site
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 lg:py-24" data-testid={`legal-${slug}`}>
        <p className="text-xs uppercase tracking-[0.25em] text-gold-deep mb-4">Legal</p>
        <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight">{doc.title}</h1>
        <p className="mt-4 text-navy/50 text-sm">Last updated: {doc.updated}</p>

        <div className="mt-12 space-y-10">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl sm:text-3xl text-navy">{s.heading}</h2>
              {s.body.map((p, j) => (
                <p key={j} className="mt-4 text-navy/70 leading-relaxed">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2">
                  {s.list.map((li, k) => (
                    <li key={k} className="flex items-start gap-3 text-navy/70">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold-deep shrink-0" /> {li}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-16 border border-gold/40 bg-gold/5 p-6">
          <p className="text-sm text-navy/70 leading-relaxed">
            <span className="font-medium text-navy">Note:</span> This document is provided as a working draft prepared for internal review. The final legal wording should be reviewed and approved by a qualified Indian legal/compliance professional before publication.
          </p>
        </div>

        <div className="mt-12 flex flex-wrap gap-4 text-sm">
          {Object.entries(LEGAL_DOCS).map(([k, d]) => (
            <Link key={k} to={`/legal/${k}`} className={`link-underline ${k === slug ? "text-gold-deep" : "text-navy/60 hover:text-navy"}`} data-testid={`legal-nav-${k}`}>
              {d.title}
            </Link>
          ))}
        </div>
      </main>

      <footer className="bg-navy text-ivory/40 text-sm py-8 text-center">
        © 2026 KRV Nexus Pvt Ltd. All rights reserved.
      </footer>
    </div>
  );
}
